/**
 * StageBoard — THE canvas/kanban view for any entity that has a stage.
 *
 * A third view mode alongside Table and Card: fixed stage columns, one glass
 * card per record, drag a card into another column to move it. Matters use it
 * first (Planned / Ongoing / Final Stages / Completed); Projects reuse it
 * verbatim — which is why nothing here knows what a matter is.
 *
 * The move is OPTIMISTIC: the card lands in the new column immediately and
 * `onMove` persists in the background. If the call resolves falsy or throws,
 * the card snaps back to where it came from, so a rejected stage (the backend
 * 400s on an unknown value) can never leave the board lying about the record.
 *
 * Drag runs on the app-wide react-dnd `DndProvider` mounted in `App.tsx` —
 * this component must not mount its own, or the two backends fight.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Spinner from '../TailwindComponents/Spinner';

export interface StageBoardColumn {
  /** Value persisted for this column — must match the backend's stage enum. */
  id: string;
  /** Already-translated column heading. */
  label: string;
  /** Optional accent for the column's hairline rail. */
  accent?: string;
}

export interface StageBoardProps<T> {
  columns: StageBoardColumn[];
  items: T[];
  getId: (item: T) => string;
  /** Current stage of the item. Anything not matching a column id lands in the first column. */
  getStage: (item: T) => string | null | undefined;
  renderCard: (item: T) => React.ReactNode;
  /**
   * Persist the move. Resolve truthy to keep the card in its new column;
   * resolve falsy (or throw) to snap it back.
   */
  onMove: (item: T, toStage: string) => Promise<unknown> | unknown;
  /** Cards a user may not move (e.g. a matter still processing). Default: all draggable. */
  canDrag?: (item: T) => boolean;
  /** Drag type — keep distinct per entity so a matter can't be dropped on a project board. */
  dragType?: string;
  isLoading?: boolean;
  /** Rendered under the last card of every column (e.g. a "Load more" button). */
  footer?: React.ReactNode;
  className?: string;
}

interface CardProps<T> {
  item: T;
  id: string;
  dragType: string;
  draggable: boolean;
  fromStage: string;
  children: React.ReactNode;
}

function BoardCard<T>({ id, dragType, draggable, fromStage, children }: CardProps<T>) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: dragType,
      item: { id, fromStage },
      canDrag: draggable,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [id, dragType, draggable, fromStage]
  );

  return (
    <div
      ref={drag as unknown as React.Ref<HTMLDivElement>}
      className={clsx(
        'transition-opacity duration-200 ease-slide',
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-default',
        isDragging && 'opacity-40'
      )}
    >
      {children}
    </div>
  );
}

interface ColumnProps {
  column: StageBoardColumn;
  dragType: string;
  count: number;
  isSaving: boolean;
  onDropCard: (id: string, fromStage: string) => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const BoardColumn: React.FC<ColumnProps> = ({
  column,
  dragType,
  count,
  isSaving,
  onDropCard,
  children,
  footer,
}) => {
  const { t } = useTranslation();
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: dragType,
      drop: (dragged: { id: string; fromStage: string }) => {
        if (dragged.fromStage !== column.id) onDropCard(dragged.id, dragged.fromStage);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [column.id, dragType, onDropCard]
  );

  return (
    <div
      ref={drop as unknown as React.Ref<HTMLDivElement>}
      className={clsx(
        'flex flex-col min-w-[17rem] w-[17rem] flex-shrink-0 rounded-lg',
        'border transition-colors duration-200 ease-slide',
        isOver && canDrop
          ? 'border-champagne/60 bg-champagne/5'
          : 'border-hairline-soft bg-surface-sunken/40'
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-hairline-soft flex-shrink-0">
        <span className="h-1.5 w-1.5 rounded-full bg-champagne flex-shrink-0" aria-hidden />
        <span className="text-[11px] font-medium text-fg-strong truncate">{column.label}</span>
        <span className="ml-auto text-[11px] tabular-nums text-fg-muted">{count}</span>
        {isSaving && <Spinner size="inline" tone="muted" />}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main p-2 space-y-2">
        {children}
        {count === 0 && (
          <p className="text-[11px] text-fg-muted italic px-1 py-6 text-center">
            {t('Nothing here yet')}
          </p>
        )}
        {footer}
      </div>
    </div>
  );
};

function StageBoard<T>({
  columns,
  items,
  getId,
  getStage,
  renderCard,
  onMove,
  canDrag,
  dragType = 'stage-board-card',
  isLoading = false,
  footer,
  className,
}: StageBoardProps<T>) {
  const { t } = useTranslation();

  /**
   * Optimistic stage per item id. Cleared for an id as soon as the item it
   * describes arrives from the server carrying that stage — keeping a stale
   * override would pin the card in place through the next refetch.
   */
  const [pending, setPending] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, true>>({});
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    setPending((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      let changed = false;
      const next: Record<string, string> = {};
      for (const item of items) {
        const id = getId(item);
        const override = prev[id];
        if (override == null) continue;
        if (getStage(item) === override) { changed = true; continue; }
        next[id] = override;
      }
      if (Object.keys(next).length !== Object.keys(prev).length) changed = true;
      return changed ? next : prev;
    });
  }, [items, getId, getStage]);

  const columnIds = useMemo(() => new Set(columns.map((c) => c.id)), [columns]);

  const stageOf = useCallback(
    (item: T): string => {
      const id = getId(item);
      const raw = pending[id] ?? getStage(item) ?? '';
      return columnIds.has(raw) ? raw : columns[0]?.id ?? '';
    },
    [pending, getId, getStage, columnIds, columns]
  );

  const grouped = useMemo(() => {
    const map: Record<string, T[]> = {};
    for (const column of columns) map[column.id] = [];
    for (const item of items) {
      const stage = stageOf(item);
      (map[stage] ?? map[columns[0]?.id ?? ''])?.push(item);
    }
    return map;
  }, [items, columns, stageOf]);

  const handleDrop = useCallback(
    async (columnId: string, id: string, fromStage: string) => {
      const item = itemsRef.current.find((candidate) => getId(candidate) === id);
      if (!item) return;

      setPending((prev) => ({ ...prev, [id]: columnId }));
      setSaving((prev) => ({ ...prev, [id]: true }));
      try {
        const result = await onMove(item, columnId);
        if (!result) {
          // Rejected — put it back where the user took it from.
          setPending((prev) => ({ ...prev, [id]: fromStage }));
        }
      } catch {
        setPending((prev) => ({ ...prev, [id]: fromStage }));
      } finally {
        setSaving((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [getId, onMove]
  );

  const savingByColumn = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const id of Object.keys(saving)) {
      const item = items.find((candidate) => getId(candidate) === id);
      if (item) map[stageOf(item)] = true;
    }
    return map;
  }, [saving, items, getId, stageOf]);

  return (
    <div
      className={clsx(
        'flex-1 min-h-0 flex gap-3 overflow-x-auto overflow-y-hidden custom-scrollbar-main px-4 pb-3',
        className
      )}
      role="group"
      aria-label={t('Stage board')}
      aria-busy={isLoading}
    >
      {columns.map((column) => {
        const columnItems = grouped[column.id] ?? [];
        return (
          <BoardColumn
            key={column.id}
            column={column}
            dragType={dragType}
            count={columnItems.length}
            isSaving={!!savingByColumn[column.id]}
            onDropCard={(id, fromStage) => handleDrop(column.id, id, fromStage)}
            footer={footer}
          >
            {columnItems.map((item) => {
              const id = getId(item);
              return (
                <BoardCard
                  key={id}
                  id={id}
                  item={item}
                  dragType={dragType}
                  draggable={canDrag ? canDrag(item) : true}
                  fromStage={column.id}
                >
                  {renderCard(item)}
                </BoardCard>
              );
            })}
          </BoardColumn>
        );
      })}
    </div>
  );
}

export default StageBoard;
